// Venue message overrides (issue #20, US-5): an owner rewrites the registration and the ready message per language,
// with a 160-character limit, a live preview and a way back to the default. Pure rules shared by the settings page
// and the API, so both refuse the same texts with the same codes. Darija is not in release 1: only the supported
// languages (fr, ar, en) take a text. Rendering with the overrides lives in templates.ts.
import { LANG_LABELS, UI, fill } from './copy';
import {
  CLOSING_LINE,
  DEFAULT_MESSAGE_TEMPLATES,
  MESSAGE_PLACEHOLDERS,
  OVERRIDE_KINDS,
  fillMessage,
  renderSms,
  type MessageContext,
  type MessageOverrides,
  type MessagePlaceholder,
  type OverrideKind,
} from './templates';
import { SUPPORTED_LANGS, type Lang, type NumberMode } from './ticket';

/** The longest message a venue may write, closing line included (spec, Localization). */
export const MESSAGE_MAX_CHARS = 160;

/**
 * The variables a venue's text must keep. The registration message names the order and the venue (US-15); the ready
 * message also names the pickup point (US-25). One platform number serves every venue, so the venue name is in the
 * body, never in the sender.
 */
export const REQUIRED_PLACEHOLDERS: Record<OverrideKind, readonly MessagePlaceholder[]> = {
  registered: ['order_number', 'venue_name'],
  ready: ['order_number', 'venue_name', 'pickup_point'],
};

/** `{{name}}` as written in a text. */
export function placeholderToken(p: MessagePlaceholder): string {
  return `{{${p}}}`;
}

export function isOverrideKind(v: unknown): v is OverrideKind {
  return typeof v === 'string' && (OVERRIDE_KINDS as readonly string[]).includes(v);
}

function isSupportedLang(v: string): v is Lang {
  return (SUPPORTED_LANGS as readonly string[]).includes(v);
}

/** Characters as a person counts them: one per code point, so an emoji or an Arabic letter with its mark counts as typed. */
export function messageLength(text: string): number {
  return Array.from(text).length;
}

/** The number the counter and the preview use: the widest one of the mode (scan wraps at 999, receipts go up to six digits). */
export const SAMPLE_ORDER_NUMBER: Record<NumberMode, string> = { scan: '123', ticket: '123456' };

/**
 * The message as a guest of this venue would receive it, for counting and previewing: the venue name, the longest
 * name among its pickup points (the counter itself when there is none) and the widest number of its mode.
 */
export function messageSample(venueName: string, pickupPointNames: readonly string[], mode: NumberMode): MessageContext {
  const longest = pickupPointNames.reduce<string | null>((best, n) => (best === null || messageLength(n) > messageLength(best) ? n : best), null);
  return { orderNumber: SAMPLE_ORDER_NUMBER[mode], venueName, pickupPoint: longest };
}

const CONTROL_CHARS = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g;
/** Embedding, override and isolate controls: a typed one could flip the order number or the venue name around. */
const BIDI_CONTROLS = /[\u202A-\u202E\u2066-\u2069]/g;

/**
 * A text as it is stored: NFC, line breaks as `\n`, no control characters, runs of spaces collapsed, at most one
 * empty line in a row, trimmed. `{{ Order_Number }}` becomes `{{order_number}}`.
 */
export function normalizeMessageText(input: string): string {
  return input
    .normalize('NFC')
    .replace(/\r\n?/g, '\n')
    .replace(/\t/g, ' ')
    .replace(CONTROL_CHARS, '')
    .replace(BIDI_CONTROLS, '')
    .replace(/\{\{\s*([A-Za-z_]+)\s*\}\}/g, (m, name: string) => ((MESSAGE_PLACEHOLDERS as readonly string[]).includes(name.toLowerCase()) ? `{{${name.toLowerCase()}}}` : m))
    .split('\n')
    .map((line) => line.replace(/ {2,}/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/** The default text of a message, with its placeholders. */
export function defaultMessageText(kind: OverrideKind, lang: Lang): string {
  return DEFAULT_MESSAGE_TEMPLATES[lang][kind];
}

/** True when a text (after normalization) is empty or the default: the venue keeps no override for it. */
export function isDefaultMessageText(kind: OverrideKind, lang: Lang, text: string): boolean {
  const t = normalizeMessageText(text);
  return t === '' || t === DEFAULT_MESSAGE_TEMPLATES[lang][kind];
}

/** The WhatsApp message a text gives with this sample: variables filled, closing line added to the ready message. */
export function messageWithSample(kind: OverrideKind, lang: Lang, text: string, sample: MessageContext): string {
  const body = fillMessage(text, sample, lang);
  return kind === 'ready' ? `${body}\n${CLOSING_LINE[lang]}` : body;
}

/** The refusals of a text; each is also a copy key. */
export const MESSAGE_OVERRIDE_ERRORS = ['message_too_long', 'message_missing_placeholder', 'message_bad_placeholder', 'message_link'] as const;
export type MessageOverrideError = (typeof MESSAGE_OVERRIDE_ERRORS)[number];

export function isMessageOverrideError(v: unknown): v is MessageOverrideError {
  return typeof v === 'string' && (MESSAGE_OVERRIDE_ERRORS as readonly string[]).includes(v);
}

export type MessageCheck =
  /** `value` is the text to store, or null when it is empty or the default (nothing stored, the default applies). */
  | { value: string | null; length: number; error?: undefined; placeholder?: undefined }
  /** `placeholder` names the variable that is missing or the braces that are wrong. */
  | { value?: undefined; length: number; error: MessageOverrideError; placeholder?: string };

const BRACED = /\{\{([^{}]*)\}\}/g;
const STRAY_BRACE = /\{+[^{}\s]*\}*|\}+/;
/** A link in a service message reads as marketing or phishing on the shared platform number: refused. */
const LINK_RE = /(?:https?:\/\/|www\.)\S/i;

/**
 * Validates one text an owner typed for `kind` in `lang`. In order: every `{{...}}` must be a known variable and no
 * brace may be left alone (`message_bad_placeholder`), the required variables must be there
 * (`message_missing_placeholder`), no link (`message_link`), and the message as the guest receives it, measured with
 * `sample` and the closing line, must stay within MESSAGE_MAX_CHARS (`message_too_long`). `length` is that count.
 */
export function checkMessageOverride(kind: OverrideKind, lang: Lang, input: string, sample: MessageContext): MessageCheck {
  const text = normalizeMessageText(input);
  if (text === '' || text === DEFAULT_MESSAGE_TEMPLATES[lang][kind]) {
    return { value: null, length: messageLength(messageWithSample(kind, lang, DEFAULT_MESSAGE_TEMPLATES[lang][kind], sample)) };
  }
  const length = messageLength(messageWithSample(kind, lang, text, sample));
  for (const m of text.matchAll(BRACED)) {
    if (!(MESSAGE_PLACEHOLDERS as readonly string[]).includes(m[1])) return { error: 'message_bad_placeholder', placeholder: m[0], length };
  }
  const stray = STRAY_BRACE.exec(text.replace(BRACED, ' '));
  if (stray) return { error: 'message_bad_placeholder', placeholder: stray[0], length };
  for (const p of REQUIRED_PLACEHOLDERS[kind]) {
    if (!text.includes(placeholderToken(p))) return { error: 'message_missing_placeholder', placeholder: placeholderToken(p), length };
  }
  if (LINK_RE.test(text)) return { error: 'message_link', length };
  if (length > MESSAGE_MAX_CHARS) return { error: 'message_too_long', length };
  return { value: text, length };
}

/** A change of the venue's texts: per language and message, the new text or null for the default. Absent entries stay as they are. */
export type MessagesPatch = Partial<Record<Lang, Partial<Record<OverrideKind, string | null>>>>;

export interface MessagesPatchError {
  error: MessageOverrideError | 'bad_setting';
  lang?: Lang;
  kind?: OverrideKind;
  placeholder?: string;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Validates the `messages` field of the settings body: `{ fr?: { registered?, ready? }, ar?: …, en?: … }`, each text
 * a string (checked with `checkMessageOverride`) or null / "" for the default. Any other language code (Darija
 * included) or message name is `bad_setting`. The first problem is returned with the language, the message and the
 * variable it is about; nothing is partially accepted.
 */
export function parseMessagesPatch(input: unknown, sample: MessageContext): { value: MessagesPatch; error?: undefined } | ({ value?: undefined } & MessagesPatchError) {
  if (!isPlainObject(input)) return { error: 'bad_setting' };
  const out: MessagesPatch = {};
  for (const [l, kinds] of Object.entries(input)) {
    if (!isSupportedLang(l)) return { error: 'bad_setting' };
    if (!isPlainObject(kinds)) return { error: 'bad_setting', lang: l };
    for (const [k, text] of Object.entries(kinds)) {
      if (!isOverrideKind(k)) return { error: 'bad_setting', lang: l };
      if (text !== null && typeof text !== 'string') return { error: 'bad_setting', lang: l, kind: k };
      const checked: MessageCheck = text === null ? { value: null, length: 0 } : checkMessageOverride(k, l, text, sample);
      if (checked.error !== undefined) return { error: checked.error, lang: l, kind: k, placeholder: checked.placeholder };
      (out[l] ??= {})[k] = checked.value;
    }
  }
  return { value: out };
}

/** The overrides after a patch: a text replaces, null removes; languages left without a text disappear. */
export function applyMessagesPatch(current: MessageOverrides, patch: MessagesPatch): MessageOverrides {
  const out: MessageOverrides = {};
  for (const l of SUPPORTED_LANGS) {
    for (const k of OVERRIDE_KINDS) {
      const next = patch[l] && k in (patch[l] as object) ? patch[l]![k] : current[l]?.[k];
      if (typeof next === 'string' && next.length > 0) (out[l] ??= {})[k] = next;
    }
  }
  return out;
}

/** The stored column: JSON in a fixed order, or null when the venue keeps every default. */
export function serializeMessageOverrides(o: MessageOverrides): string | null {
  const compact: MessageOverrides = {};
  for (const l of SUPPORTED_LANGS) {
    for (const k of OVERRIDE_KINDS) {
      const t = o[l]?.[k];
      if (typeof t === 'string' && t.length > 0) (compact[l] ??= {})[k] = t;
    }
  }
  return Object.keys(compact).length > 0 ? JSON.stringify(compact) : null;
}

/**
 * Reads the stored column. Entries that are not a supported language and message, or that lost a required
 * variable, are dropped, so a hand-edited row can never send a message without its number: the default goes out.
 */
export function messageOverridesFromJson(text: string | null | undefined): MessageOverrides {
  if (!text) return {};
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return {};
  }
  if (!isPlainObject(raw)) return {};
  const out: MessageOverrides = {};
  for (const l of SUPPORTED_LANGS) {
    const kinds = raw[l];
    if (!isPlainObject(kinds)) continue;
    for (const k of OVERRIDE_KINDS) {
      const t = kinds[k];
      if (typeof t === 'string' && t.length > 0 && REQUIRED_PLACEHOLDERS[k].every((p) => t.includes(placeholderToken(p)))) (out[l] ??= {})[k] = t;
    }
  }
  return out;
}

/** What the settings page shows under a text while it is typed. */
export interface MessagePreview {
  /** The WhatsApp message with the sample values, closing line included for the ready message. */
  text: string;
  /** Its length, the number the counter shows against MESSAGE_MAX_CHARS. */
  length: number;
  /** True when the text is empty or the default: nothing is stored and the default applies. */
  isDefault: boolean;
  /** Ready message only: the backup SMS this text gives, and whether the venue's text or the short default goes out. */
  sms: { text: string; source: 'override' | 'default' } | null;
}

/**
 * The live preview of a draft. An empty draft previews the default. The draft is shown as typed even when it does
 * not validate yet (the error shows next to it); the SMS follows the same rule as at send time.
 */
export function previewMessage(kind: OverrideKind, lang: Lang, draft: string, sample: MessageContext): MessagePreview {
  const normalized = normalizeMessageText(draft);
  const isDefault = normalized === '' || normalized === DEFAULT_MESSAGE_TEMPLATES[lang][kind];
  const text = messageWithSample(kind, lang, isDefault ? DEFAULT_MESSAGE_TEMPLATES[lang][kind] : normalized, sample);
  const sms = kind === 'ready' ? renderSms(lang, sample, isDefault ? null : { [lang]: { ready: normalized } }) : null;
  return { text, length: messageLength(text), isDefault, sms: sms ? { text: sms.text, source: sms.source } : null };
}

/** The copy of a message refusal in `lang`, with the limit or the variable filled in. */
export function messageErrorCopy(lang: Lang, error: MessageOverrideError, placeholder?: string): string {
  return fill(UI[lang][error], { max: String(MESSAGE_MAX_CHARS), placeholder: placeholder ?? '' });
}

/** The name of a message on the settings page. */
export function messageLabel(lang: Lang, kind: OverrideKind): string {
  return kind === 'ready' ? UI[lang].message_ready : UI[lang].message_registered;
}

/** A refusal with the text it is about, for the line above the save button: "Français, Message « commande prête » : …". */
export function messageProblemCopy(uiLang: Lang, field: { lang?: Lang; kind?: OverrideKind }, problem: string): string {
  if (!field.lang || !field.kind) return problem;
  return fill(UI[uiLang].message_problem, { lang: LANG_LABELS[field.lang], message: messageLabel(uiLang, field.kind), problem });
}
